import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../hooks/useAuth';
import { creditNotesService } from '../../../services/creditNotesService';

const CreditNotesManagement = () => {
  const { user } = useAuth();
  const [creditNotes, setCreditNotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Form state for new credit note
  const [formData, setFormData] = useState({
    customerId: '',
    creditReason: 'product_return',
    reasonDescription: '',
    issueDate: new Date()?.toISOString()?.split('T')?.[0],
    description: '',
    notes: '',
    currencyCode: 'USD',
    lineItems: []
  });

  // Line item form state
  const [newLineItem, setNewLineItem] = useState({
    itemId: '',
    itemDescription: '',
    quantity: 1,
    unitPrice: 0,
    taxRate: 0,
    taxAmount: 0,
    notes: ''
  });

  const organizationId = user?.user_metadata?.organization_id || 'f47ac10b-58cc-4372-a567-0e02b2c3d479';

  const creditReasons = [
    { value: 'product_return', label: 'Product Return' },
    { value: 'pricing_adjustment', label: 'Pricing Adjustment' },
    { value: 'service_issue', label: 'Service Issue' },
    { value: 'overpayment', label: 'Overpayment' },
    { value: 'discount', label: 'Discount' },
    { value: 'other', label: 'Other' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'issued', label: 'Issued' },
    { value: 'applied', label: 'Applied' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [creditNotesData, customersData, itemsData] = await Promise.all([
        creditNotesService?.getAll(organizationId),
        creditNotesService?.getCustomers(organizationId),
        creditNotesService?.getItems(organizationId)
      ]);

      setCreditNotes(creditNotesData);
      setCustomers(customersData);
      setItems(itemsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(`Failed to load data: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      setError('');
      setLoading(true);

      // Calculate totals
      const subtotal = formData?.lineItems?.reduce((sum, item) => sum + (item?.quantity * item?.unitPrice), 0);
      const taxAmount = formData?.lineItems?.reduce((sum, item) => sum + item?.taxAmount, 0);
      const totalCreditAmount = subtotal + taxAmount;

      const creditNoteData = {
        ...formData,
        subtotal,
        taxAmount,
        totalCreditAmount
      };

      await creditNotesService?.create(creditNoteData, organizationId);
      
      setSuccess('Credit Note created successfully!');
      setIsFormOpen(false);
      resetForm();
      await loadData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creating credit note:', err);
      setError(`Failed to create credit note: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customerId: '',
      creditReason: 'product_return',
      reasonDescription: '',
      issueDate: new Date()?.toISOString()?.split('T')?.[0],
      description: '',
      notes: '',
      currencyCode: 'USD',
      lineItems: []
    });
    setNewLineItem({
      itemId: '',
      itemDescription: '',
      quantity: 1,
      unitPrice: 0,
      taxRate: 0,
      taxAmount: 0,
      notes: ''
    });
  };

  const addLineItem = () => {
    if (!newLineItem?.itemDescription || newLineItem?.quantity <= 0) {
      setError('Please fill in all required line item fields');
      return;
    }

    const lineTotal = newLineItem?.quantity * newLineItem?.unitPrice;
    const taxAmount = (lineTotal * newLineItem?.taxRate) / 100;

    const lineItem = {
      ...newLineItem,
      lineTotal,
      taxAmount,
      id: Date.now() // Temporary ID for frontend
    };

    setFormData(prev => ({
      ...prev,
      lineItems: [...prev?.lineItems, lineItem]
    }));

    setNewLineItem({
      itemId: '',
      itemDescription: '',
      quantity: 1,
      unitPrice: 0,
      taxRate: 0,
      taxAmount: 0,
      notes: ''
    });
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev?.lineItems?.filter((_, i) => i !== index)
    }));
  };

  const handleItemSelect = (itemId) => {
    const selectedItem = items?.find(item => item?.id === itemId);
    if (selectedItem) {
      setNewLineItem(prev => ({
        ...prev,
        itemId: itemId,
        itemDescription: selectedItem?.itemName,
        unitPrice: selectedItem?.sellingPrice,
        taxRate: selectedItem?.taxRate
      }));
    }
  };

  const handleUpdateStatus = async (creditNoteId, status) => {
    try {
      setError('');
      await creditNotesService?.updateStatus(creditNoteId, status);
      setSuccess(`Credit Note status updated to ${status}!`);
      await loadData();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setError(`Failed to update status: ${err?.message}`);
    }
  };

  const handleDelete = async (creditNoteId) => {
    if (!confirm('Are you sure you want to delete this credit note? This action cannot be undone.')) {
      return;
    }

    try {
      setError('');
      await creditNotesService?.delete(creditNoteId);
      setSuccess('Credit Note deleted successfully!');
      await loadData();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting credit note:', err);
      setError(`Failed to delete credit note: ${err?.message}`);
    }
  };

  // Filter credit notes based on search term and status
  const filteredCreditNotes = creditNotes?.filter(creditNote => {
    const matchesSearch = creditNote?.creditNoteNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         creditNote?.customer?.customerName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         creditNote?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || creditNote?.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount || 0);
  };

  if (loading && !isFormOpen) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-foreground">Credit Notes Management</h3>
          <p className="text-sm text-muted-foreground">
            Create and manage credit notes for refunds, returns, and adjustments
          </p>
        </div>
        <Button
          onClick={() => setIsFormOpen(true)}
          className="flex items-center space-x-2"
        >
          <Icon name="Plus" size={16} />
          <span>Add Credit Note</span>
        </Button>
      </div>
      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="CheckCircle" size={16} className="text-green-600 mr-2" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        </div>
      )}
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={16} className="text-red-600 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search credit notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="w-full sm:w-48">
          <Select
            value={statusFilter}
            onValueChange={setStatusFilter}
            options={statusOptions}
            placeholder="Filter by status"
          />
        </div>
      </div>
      {/* Credit Notes List */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        {filteredCreditNotes?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-foreground font-medium mb-2">No Credit Notes Found</p>
            <p className="text-muted-foreground mb-4">
              {creditNotes?.length === 0 
                ? "Get started by creating your first credit note" :"No credit notes match your current filters"
              }
            </p>
            {creditNotes?.length === 0 && (
              <Button onClick={() => setIsFormOpen(true)} variant="outline">
                <Icon name="Plus" size={16} className="mr-2" />
                Create Credit Note
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Credit Note
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-border">
                {filteredCreditNotes?.map((creditNote) => (
                  <tr key={creditNote?.id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {creditNote?.creditNoteNumber}
                        </div>
                        {creditNote?.description && (
                          <div className="text-sm text-muted-foreground">
                            {creditNote?.description?.length > 30 
                              ? `${creditNote?.description?.substring(0, 30)}...`
                              : creditNote?.description
                            }
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {creditNote?.customer?.customerName || creditNote?.customer?.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {creditNote?.customer?.customerNumber}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-foreground capitalize">
                        {creditNote?.creditReason?.replace('_', ' ')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-foreground">
                        {formatCurrency(creditNote?.totalCreditAmount)}
                      </div>
                      {creditNote?.remainingCredit > 0 && (
                        <div className="text-sm text-muted-foreground">
                          {formatCurrency(creditNote?.remainingCredit)} remaining
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                        ${creditNote?.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                          creditNote?.status === 'issued' ? 'bg-blue-100 text-blue-800' :
                          creditNote?.status === 'applied' ? 'bg-green-100 text-green-800' :
                          creditNote?.status === 'cancelled'? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                        {creditNote?.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(creditNote.issueDate)?.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {creditNote?.status === 'draft' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateStatus(creditNote?.id, 'issued')}
                            >
                              Issue
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(creditNote?.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Icon name="Trash2" size={14} />
                            </Button>
                          </>
                        )}
                        {creditNote?.status === 'issued' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateStatus(creditNote?.id, 'cancelled')}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Add Credit Note Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">Add Credit Note</h3>
                <button
                  onClick={() => {
                    setIsFormOpen(false);
                    resetForm();
                    setError('');
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Customer *
                  </label>
                  <Select
                    value={formData?.customerId}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, customerId: value }))}
                    options={customers?.map(customer => ({
                      value: customer?.id,
                      label: `${customer?.customerName || customer?.name} (${customer?.customerNumber})`
                    }))}
                    placeholder="Select customer"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Credit Reason *
                  </label>
                  <Select
                    value={formData?.creditReason}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, creditReason: value }))}
                    options={creditReasons}
                    placeholder="Select reason"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Issue Date *
                  </label>
                  <Input
                    type="date"
                    value={formData?.issueDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, issueDate: e?.target?.value }))}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Currency
                  </label>
                  <Select
                    value={formData?.currencyCode}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, currencyCode: value }))}
                    options={[
                      { value: 'USD', label: 'USD - US Dollar' },
                      { value: 'EUR', label: 'EUR - Euro' },
                      { value: 'GBP', label: 'GBP - British Pound' }
                    ]}
                    placeholder="Select currency"
                  />
                </div>
              </div>

              {/* Reason Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Reason Description
                </label>
                <Input
                  type="text"
                  value={formData?.reasonDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, reasonDescription: e?.target?.value }))}
                  placeholder="Detailed explanation of the credit reason"
                />
              </div>

              {/* Line Items Section */}
              <div className="border border-border rounded-lg p-4">
                <h4 className="text-md font-medium text-foreground mb-4">Line Items</h4>
                
                {/* Add Line Item Form */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                  <div>
                    <Select
                      value={newLineItem?.itemId}
                      onValueChange={handleItemSelect}
                      options={items?.map(item => ({
                        value: item?.id,
                        label: `${item?.itemName} (${item?.itemNumber})`
                      }))}
                      placeholder="Select item"
                    />
                  </div>
                  <div>
                    <Input
                      type="text"
                      value={newLineItem?.itemDescription}
                      onChange={(e) => setNewLineItem(prev => ({ ...prev, itemDescription: e?.target?.value }))}
                      placeholder="Item description"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      value={newLineItem?.quantity}
                      onChange={(e) => setNewLineItem(prev => ({ ...prev, quantity: parseFloat(e?.target?.value) || 0 }))}
                      placeholder="Quantity"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      value={newLineItem?.unitPrice}
                      onChange={(e) => setNewLineItem(prev => ({ ...prev, unitPrice: parseFloat(e?.target?.value) || 0 }))}
                      placeholder="Unit Price"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <Button
                      type="button"
                      onClick={addLineItem}
                      variant="outline"
                      size="sm"
                    >
                      <Icon name="Plus" size={14} />
                    </Button>
                  </div>
                </div>

                {/* Line Items List */}
                {formData?.lineItems?.length > 0 && (
                  <div className="space-y-2">
                    {formData?.lineItems?.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">
                            {item?.itemDescription}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Qty: {item?.quantity} × {formatCurrency(item?.unitPrice)} = {formatCurrency(item?.quantity * item?.unitPrice)}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeLineItem(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Icon name="Trash2" size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Additional Information */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description
                  </label>
                  <Input
                    type="text"
                    value={formData?.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
                    placeholder="Brief description of the credit note"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData?.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e?.target?.value }))}
                    placeholder="Additional notes or comments"
                    rows={3}
                    className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-4 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsFormOpen(false);
                    resetForm();
                    setError('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !formData?.customerId}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    'Create Credit Note'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditNotesManagement;